import React from "react";

import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import { alertCategoriesTranslations, AlertConfig, HypoglycemiaAlertParametersCGM } from "@/models/AlertModel.ts";
import { HappenedInTheLastXDays } from "@/pages/patient-settings/alert-settings/inputs/HappenedInTheLastXDays.tsx";
import { NumberOfOccurrences } from "@/pages/patient-settings/alert-settings/inputs/NumberOfOccurrences.tsx";
import { SelectType } from "@/pages/patient-settings/alert-settings/inputs/SelectType.tsx";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { TextToast } from "@/uiKit/atoms/Toast.tsx";
import { formatNumber } from "@/utils/mathUtils.ts";

export type AlertHypoglycemiaCGMSettingsProps = {
  patientId: string;
  alertConfig: AlertConfig;
  parameters: HypoglycemiaAlertParametersCGM;
};

export const AlertHypoglycemiaCGMSettings: React.FC<AlertHypoglycemiaCGMSettingsProps> = ({
  parameters,
  patientId,
  alertConfig,
}) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();
  const editAlertConfig = Queries.alerts.useEditAlertConfig();

  const updateParameters = (value: unknown, key: keyof HypoglycemiaAlertParametersCGM) => {
    editAlertConfig.mutate(
      {
        patientId,
        alertConfig: {
          id: alertConfig.id,
          parameters: { ...parameters, [key]: value },
        },
      },
      {
        onSuccess: () =>
          TextToast.success(
            t("alertConfig.conditionsSuccess", {
              alert: t(alertCategoriesTranslations.hypoglycemia),
            }),
          ),
        onError: () =>
          TextToast.error(
            t("alertConfig.conditionsError", {
              alert: t(alertCategoriesTranslations.hypoglycemia),
            }),
          ),
      },
    );
  };

  return (
    <div className={styles.container}>
      <NumberOfOccurrences
        id={`alert-config-${alertConfig.id}-occurrences`}
        textBefore={t("alertConfig.moreThan")}
        textAfter={t("glycemia.hypoglycemia", {
          count: parameters.maxNumberOfEvents,
        })}
        onCountChange={count => updateParameters(count, "maxNumberOfEvents")}
        minCount={1}
        maxCount={999}
        count={formatNumber(parameters.maxNumberOfEvents, 0)}
      />
      <SelectType
        id={`alert-config-${alertConfig.id}-severity`}
        onSelectChange={type => updateParameters(type, "severeOnly")}
        options={[
          { value: false, text: t("alertConfig.lowOrVeryLow") },
          { value: true, text: t("alertConfig.veryLow") },
        ]}
        selected={parameters.severeOnly}
      />
      <HappenedInTheLastXDays
        id={`alert-config-${alertConfig.id}-days`}
        onDaysChange={days => updateParameters(days, "numberOfDays")}
        minDays={1}
        maxDays={365}
        days={parameters.numberOfDays}
      />
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `,
});
